import React, { useState } from "react";

import DownloadLink from "../../components/DownloadLink";
import { canCheckCinetPayStatus, isCinetPayCreationLocked } from "../services/cinetPayAttempt";
import { examDocumentFilename } from "../services/examDownloadFilename";

const documentLabels = {
  pdf: "PDF sujet",
  docx: "Document DOCX",
  correction_pdf: "PDF corrigé",
};

const documentDownloads = {
  pdf: { endpoint: "pdf", filename: "sujet.pdf", mimeType: "application/pdf" },
  docx: { endpoint: "docx", filename: "sujet.docx", mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" },
  correction_pdf: { endpoint: "correction", filename: "corrige.pdf", mimeType: "application/pdf" },
};

export default function ExamGenerationResult({ exam, wallet, paymentState, cinetPayState, refreshing, refreshError, onPay, onCinetPay, onRefreshCinetPay, onRefresh, onNewExam }) {
  const [confirming, setConfirming] = useState(false);
  const [confirmingCinetPay, setConfirmingCinetPay] = useState(false);
  const documents = exam.documents || {};
  const paid = exam.paid || exam.est_payee || exam.status === "PAYEE" || exam.statut === "PAYEE";
  const paying = paymentState.status === "submitting";
  const cinetPayInitializing = cinetPayState.status === "INITIALIZING";
  const cinetPayActive = ["PENDING", "PROCESSING"].includes(cinetPayState.status);
  const cinetPayCreationLocked = isCinetPayCreationLocked(cinetPayState.status);
  const balance = wallet?.balance;
  const currency = wallet?.currency || "FCFA";
  const confirmPayment = () => {
    setConfirming(false);
    onPay();
  };
  const confirmCinetPayPayment = () => {
    setConfirmingCinetPay(false);
    onCinetPay();
  };
  return (
    <section className="ql-generation-result">
      <div className="ql-generation-success-icon"><i className="bi bi-check-lg"></i></div>
      <span className="ql-teacher-eyebrow">Génération terminée</span>
      <h2>Épreuve générée avec succès</h2>
      <p className={`ql-generation-payment-notice ${paid ? "paid" : ""}`}>
        {paid ? "Paiement effectué avec succès" : "Paiement requis pour télécharger les documents."}
      </p>

      <dl className="ql-generation-details">
        <div><dt>Numéro</dt><dd>#{exam.id}</dd></div>
        <div><dt>Titre</dt><dd>{exam.title || "—"}</dd></div>
        <div><dt>Matière</dt><dd>{exam.subject || "—"}</dd></div>
        <div><dt>Classe</dt><dd>{exam.class || "—"}</dd></div>
        <div><dt>Palier</dt><dd>{exam.tier}</dd></div>
        <div><dt>Montant</dt><dd>{exam.amount} {currency}</dd></div>
        <div><dt>Génération</dt><dd>{exam.generation_status || "—"}</dd></div>
        <div><dt>Paiement</dt><dd>{paid ? "Payé" : exam.status || exam.statut || "EN_ATTENTE_PAIEMENT"}</dd></div>
      </dl>

      <div className="ql-wallet-payment-summary">
        <div><span>Solde disponible</span><strong>{Number.isFinite(balance) ? balance : "—"} {currency}</strong></div>
        <div><span>Montant à payer</span><strong>{exam.amount} {currency}</strong></div>
        <div><span>Palier</span><strong>{exam.tier} · {exam.tier === "PREMIUM" ? "300" : "200"} {currency}</strong></div>
      </div>

      {!paid && !confirming && !confirmingCinetPay && (
        <div className="ql-payment-methods">
          <button type="button" className="btn btn-success ql-wallet-pay-button" onClick={() => setConfirming(true)} disabled={paying || cinetPayActive}>
            {paying ? "Paiement en cours…" : "Payer avec mon portefeuille"}
          </button>
          <button type="button" className="btn btn-primary ql-cinetpay-button" onClick={() => setConfirmingCinetPay(true)} disabled={cinetPayCreationLocked}>
            {cinetPayInitializing ? "Initialisation…" : cinetPayActive ? "Paiement CinetPay en cours…" : "Payer avec CinetPay"}
          </button>
        </div>
      )}
      {!paid && confirming && (
        <div className="ql-wallet-confirmation" role="dialog" aria-modal="true" aria-labelledby="ql-wallet-confirmation-title">
          <h3 id="ql-wallet-confirmation-title">Confirmer le paiement de {exam.amount} {currency} avec votre portefeuille ?</h3>
          <div>
            <button type="button" className="btn btn-success" onClick={confirmPayment} disabled={paying}>{paying ? "Paiement en cours…" : "Confirmer"}</button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirming(false)} disabled={paying}>Annuler</button>
          </div>
        </div>
      )}
      {!paid && confirmingCinetPay && (
        <div className="ql-wallet-confirmation ql-cinetpay-confirmation" role="dialog" aria-modal="true" aria-labelledby="ql-cinetpay-confirmation-title">
          <h3 id="ql-cinetpay-confirmation-title">Vous allez être redirigé vers CinetPay pour effectuer le paiement de {exam.amount} FCFA.</h3>
          <div>
            <button type="button" className="btn btn-primary" onClick={confirmCinetPayPayment} disabled={cinetPayInitializing}>Continuer</button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirmingCinetPay(false)} disabled={cinetPayInitializing}>Annuler</button>
          </div>
        </div>
      )}
      {paymentState.error && <div className="ql-generation-error" role="alert"><span>{paymentState.error}</span></div>}
      {cinetPayState.status === "SUCCESS" && <div className="ql-cinetpay-state success" role="status">Paiement CinetPay effectué avec succès.</div>}
      {cinetPayActive && <div className="ql-cinetpay-state pending" role="status">Paiement CinetPay en attente de confirmation.</div>}
      {cinetPayState.status === "INITIALIZING" && <div className="ql-cinetpay-state pending" role="status">Le statut de l’initialisation n’a pas encore pu être confirmé.</div>}
      {cinetPayState.error && <div className="ql-cinetpay-state error" role="alert">{cinetPayState.error}</div>}
      {!paid && canCheckCinetPayStatus(cinetPayState.status) && (
        <button type="button" className="btn btn-outline-primary ql-cinetpay-refresh" onClick={onRefreshCinetPay} disabled={cinetPayState.checking}>
          {cinetPayState.checking ? "Vérification…" : "Actualiser le statut"}
        </button>
      )}

      <div className="ql-generation-documents">
        <h3>Documents générés</h3>
        {Object.entries(documentLabels).map(([kind, label]) => (
          <article key={kind} className={documents[kind] ? "available" : "unavailable"}>
            <i className={`bi ${documents[kind] ? "bi-file-earmark-check" : "bi-file-earmark-x"}`}></i>
            <div><strong>{label}</strong><span>{documents[kind] ? "Généré" : "Non disponible avec ce palier"}</span></div>
            {paid && documents[kind] ? (
              <DownloadLink
                url={`/api/teacher/exams/${exam.id}/files/${documentDownloads[kind].endpoint}/`}
                filename={examDocumentFilename(exam, kind)}
                mimeType={documentDownloads[kind].mimeType}
                authenticated
                className="ql-generation-download"
              >
                Télécharger
              </DownloadLink>
            ) : (
              <button type="button" disabled title={documents[kind] ? "Paiement requis" : "Document indisponible"}>
                {documents[kind] ? "Paiement requis" : "Indisponible"}
              </button>
            )}
          </article>
        ))}
      </div>

      {refreshError && <div className="ql-exam-inline-state error" role="alert"><span>{refreshError}</span></div>}
      <div className="ql-generation-result-actions">
        <button type="button" className="btn btn-outline-primary" onClick={onRefresh} disabled={refreshing}>{refreshing ? "Actualisation…" : "Actualiser le statut"}</button>
        <button type="button" className="btn btn-primary" onClick={onNewExam}>Créer une nouvelle épreuve</button>
      </div>
    </section>
  );
}
